import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import Tooltip from "../tooltips/Tooltip";
import TexturedMesh from "../TexturedMesh";

import GlobalEvent from "js-events-listener";
import { getColour } from "../Customisation";

/**
 * Hotspots are clickable areas with varying actions
 * This class is used as a base for all types
 */
export default class BaseHotspot extends TexturedMesh {
  renderScale;
  distance;

  /**
   * Creates a hotspot based on the normalised position
   *
   * @param player
   * @param config
   */
  constructor(player, config) {
    const normPos = new THREE.Vector2(config.u, config.v);

    let _options = config._options ?? {};
    delete config._options;

    const renderScale = _options.scale ?? 1;
    const geometry = new THREE.PlaneBufferGeometry(
      45 * renderScale,
      45 * renderScale,
    );

    super(player, geometry);
    this.material.transparent = true;

    this.material.depthWrite = false;
    this._config = config;

    this.renderScale = renderScale;
    this.distance = _options.distance ?? 500;

    // NormPos is the normalized position, so need to work out the world coords
    const pos = this.normToWorld(normPos);
    this.position.x = pos.x;
    this.position.y = pos.y;
    this.position.z = pos.z;

    if (player.isSpinning) {
      this.material.opacity = 0;
    }

    this.player = player;

    this.actualScale = this.scale.x;
    // Face the object towards the user
    this.lookAt(new THREE.Vector3(0, 0, 0));

    this._isHover = false;
    this._isSpinner = false;
    this._isActive = false;

    // Create tooltip to be visible when hovering
    this._hasTooltip = false;

    if (!!config.tooltip && String(config.tooltip).trim() !== ""  && !config.permaTooltip) {
      this._hasTooltip = true;

      this._tooltip = new Tooltip(player, config.tooltip);
    }

    GlobalEvent.on(
      "BASE_HOTSPOT_HOVER_" + this._config.uuid,
      ({ isHover, clientX, clientY }) => {
        this.setHover(isHover, true, clientX, clientY);
      },
    );
  }

  /**
   * Converts the normalized position to world coordinates.
   *
   * @param {THREE.Vector2} normPos
   * @return {THREE.Vector3} world coordinates
   */
  normToWorld(normPos) {
    var a = new THREE.Vector3(0, -500, 0);
    var b = new THREE.Euler(0, 2 * normPos.x * Math.PI, normPos.y * Math.PI);

    a.applyEuler(b);

    return a;
  }

  /**
   * Returns the hotspot uuid
   *
   * @returns {string} uuid
   */
  getUUID() {
    return this._config.uuid;
  }

  /**
   * To be called when hover state should change.
   * This changes the opacity of the hotspot.
   *
   * @param {bool} isHover
   */
  setHover(
    isHover,
    fromTourEvent = false,
    clientX = undefined,
    clientY = undefined,
  ) {
    if (this._isHover !== isHover && !this._isActive) {
      // Check if the state has changed, if so, start a tween
      if (!fromTourEvent) {
        if (this._hoveringTimeout) clearTimeout(this._hoveringTimeout);
        this._hoveringTimeout = setTimeout(() => {
          GlobalEvent.emit("BASE_HOTSPOT_HOVER", {
            isHover,
            uuid: this.getUUID(),
            clientX: this._clientX,
            clientY: this._clientY,
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
          });
        }, 500);
      } else if (!!clientX && !!clientY && this._hasTooltip) {
        this._clientX = clientX;
        this._clientY = clientY;
        this._tooltip.onMouseMove({ clientX, clientY });
      }

      this._isHover = isHover;
      const targetColorHex =
        isHover && !this._isSpinner ? getColour() : 0xffffff;
      const targetColor = new THREE.Color(targetColorHex);

      const colorTween = new TWEEN.Tween(this.material.color).to(
        targetColor,
        600,
      );
      colorTween.easing(TWEEN.Easing.Elastic.Out);
      colorTween.start();

      let largerScale = 1.3 * this.actualScale;
      const targetScale = isHover
        ? { x: largerScale, y: largerScale, z: largerScale }
        : this.scale;

      const tween = new TWEEN.Tween(this.scale).to(targetScale, 400);
      tween.easing(TWEEN.Easing.Elastic.Out);
      tween.start();

      if (this.player.sceneManager.activeScene()?.SCENE_NAME === "SceneSplit") {
        if (this._hasTooltip) this._tooltip.setVisibility(false);
        return;
      }

      if (this._hasTooltip) {
        if (!this._isActive) {
          if (["device", "stereo"].includes(this.player.viewerMode)) {
            this._tooltip.alignToCenter();
          }
          this._tooltip.setVisibility(isHover);
        } else {
          this._tooltip.setVisibility(false);
        }
      }
    }
  }

  /**
   * Update coordinates when moved in editor
   */
  move() {
    const normPos = new THREE.Vector2(this._config.u, this._config.v); // NormPos is the normalized position, so need to work out the world coords
    const pos = this.normToWorld(normPos);
    this.position.x = pos.x;
    this.position.y = pos.y;
    this.position.z = pos.z;
    this.lookAt(new THREE.Vector3(0, 0, 0));
  }

  /**
   * Update the tooltip
   * @param {String} tooltipName
   */
  updateTooltip(tooltipName) {
    this._config = { ...this._config, tooltip: tooltipName };
    const hasText = !!tooltipName && String(tooltipName).trim() !== "";

    if (this._hasTooltip) {
      if (hasText) {
        this._tooltip.update(tooltipName);
      } else {
        this._tooltip.destroy();
        this._tooltip = null;
        this._hasTooltip = false;
      }
    } else if (hasText && !this._config.permaTooltip) {
      this._hasTooltip = true;
      this._tooltip = new Tooltip(this.player, tooltipName);
    }
  }

  /**
   * This is the function that is run when a hotspot is clicked on.
   * This should be overridden by child classes.
   *
   * @param {MouseEvent} event
   */
  action(event) {
    console.info("BaseHotspot: action triggered");
  }

  _clientX;
  _clientY;

  /**
   * Triggered whenever the users mouse moves.
   * This is used to keep the tooltip in position
   */
  onMouseMove(event) {
    if (this._hasTooltip) {
      this._clientX = event.clientX;
      this._clientY = event.clientY;
      this._tooltip.onMouseMove(event);
    }
  }

  /**
   * Called just before the hotspot is to be removed.
   * Used to cleanup.
   */
  destroy() {
    if (this._hasTooltip) {
      this._tooltip.destroy();
    }
    this.geometry.dispose();
    this.material.dispose();
  }

  getOpacity() {
    if (!this.active) return this.material.opacity;
  }

  setOpacity(opacity) {
    if (!this.active)
      this.material.opacity = this.player.isSpinning ? 0 : opacity;
  }

  changeToSpinner() {
    // stop hover effects and fadeout
    this._isSpinner = true;

    this._isLoaded = false;
    this.setOpacity(0);

    setTimeout(() => {
      if (!this._isLoaded) {
        this.material.color = new THREE.Color(0xffffff);

        const video = document.createElement("video");
        video.crossOrigin = "anonymous";

        video.src =
          "https://theatro360-manual-assets.s3-us-west-2.amazonaws.com/spinner.webm";
        video.loop = true;
        video.muted = true;
        video.playsinline = true;
        video.load();
        // Only autoplay if not iOS Safari
        const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) &&
          !window.MSStream &&
          /Safari/.test(navigator.userAgent);
        if (!isIOSSafari) {
          video.play();
        }

        const texture = new THREE.VideoTexture(video);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.format = THREE.RGBAFormat;

        this.material.dispose();
        this.material.map.dispose();
        this.material.map = texture;
        this.material.transparent = true;
        this.material.depthWrite = false;

        const opacity = { value: 0 };
        const opacityTween = new TWEEN.Tween(opacity).to(
          {
            value: 1,
          },
          1000,
        );
        opacityTween.onUpdate(() => {
          this.setOpacity(opacity.value);
          // Can't get the scaling to work :/
          // this.scale.x = opacity.value * this.actualScale;
          // this.scale.y = opacity.value * this.actualScale;
          // this.scale.z = opacity.value * this.actualScale;
        });
        opacityTween.start();
      }
    }, 500);
  }

  setActive() {
    if (this._hasTooltip) this._tooltip.setVisibility(false);
    this._isActive = true;
    this.setOpacity(0);
  }

  setInactive() {
    this._isActive = false;
    this.setOpacity(1);
  }

  async loadIcon(icon, backup) {
    this.backupIcon = backup;
    const media = x => `https://manage.theatro360.com/api/v2/media/${x}`;
    const asset = x => `${window.location.origin}/tour/assets/hotspots/${x}`;
    try {
      // if custom icon, fetch it
      if (icon) {
        if (icon.url) {
          this.loadTexture({ url: asset(icon.url) });
        } else {
          const res = await fetch(media(icon.id));
          if (res.status >= 400)
            throw new Error(`fetch failed with ${res.status}`);
          const data = await res.json();
          let url = data.public_url;
          if (url.includes("imgix") || url.includes("imgo")) {
            url += `&w=256&h=256&fit=crop`;
          }
          this.loadTexture({ url });
        }
      } else {
        this.loadTexture({ url: asset(backup) });
      }
    } catch (e) {
      console.warn(`Error fetching icon: ${e.message}`);
      this.loadTexture({ url: asset(backup) });
    }
  }

  updateIcon() {
    this.loadIcon(this._config.icon, this.backupIcon);
  }

  lookAtMeInterval;
  async lookAtMe(name = "", opts) {
    try {
      const { x, z } = this.position;
      let angle = Math.atan2(z, x);
      // realign (atan2 is from the x axis for some reason)
      angle = Math.PI - angle;
      if (angle < -Math.PI) {
        angle += 2 * Math.PI;
      } else if (angle > Math.PI) {
        angle -= 2 * Math.PI;
      }
      await this.player.setCameraAngle(name, angle, opts);
    } catch (error) {
      console.error("lookAtMe error", error);
    }
  }
}
